import React, { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { getDoc, doc, collection, onSnapshot } from 'firebase/firestore';
import { auth, db } from './firebase/config';
import { addPerson, updatePerson, deletePerson } from './firebase/firestoreService';
import LoginPage from './components/LoginPage';
import Dashboard from './components/Dashboard';
import StoresPage from './components/StoresPage';
import SettingsPage from './components/SettingsPage';
import Analytics from './components/Analytics';
import TwoFactorSetup from './components/TwoFactorSetup';
import TwoFactorVerify from './components/TwoFactorVerify';
import './styles/global.css';

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTwoFASetup, setShowTwoFASetup] = useState(false);
  const [showTwoFAVerify, setShowTwoFAVerify] = useState(false);

  // Check auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('onAuthStateChanged triggered, user:', user?.email);
      if (user) {
        try {
          // Check if 2FA is enabled
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          
          // 2FA yoksa setup göster
          if (!userDoc.exists()) {
            console.log('User doc does not exist, showing 2FA setup');
            setShowTwoFASetup(true);
            setShowTwoFAVerify(false);
            setIsLoggedIn(false); // FALSE - 2FA tamamlanınca true olacak
            setLoading(false);
          } else if (!userDoc.data()?.totpEnabled) {
            console.log('2FA not enabled, showing 2FA setup');
            setShowTwoFASetup(true);
            setShowTwoFAVerify(false);
            setIsLoggedIn(false); // FALSE - 2FA tamamlanınca true olacak
            setLoading(false);
          } else {
            // 2FA var, verification göster
            console.log('2FA enabled, showing verification');
            setShowTwoFASetup(false);
            setShowTwoFAVerify(true);
            setIsLoggedIn(false); // FALSE - Kod girilince true olacak
            setLoading(false);
          }
        } catch (error) {
          console.error('Error checking 2FA status:', error);
          setShowTwoFASetup(true);
          setShowTwoFAVerify(false);
          setIsLoggedIn(false);
          setLoading(false);
        }
      } else {
        setIsLoggedIn(false);
        setShowTwoFASetup(false);
        setShowTwoFAVerify(false);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  /// Load people from Firestore (Real-time)
  useEffect(() => {
    // SADECE 2FA tamamlandıysa load et
    if (isLoggedIn && !showTwoFASetup && !showTwoFAVerify && !loading) {
      console.log('Setting up real-time people listener...');
      
      const peopleRef = collection(db, 'people');
      
      const unsubscribe = onSnapshot(peopleRef, (snapshot) => {
        const peopleData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log('People updated from Firestore:', peopleData.length);
        setPeople(peopleData);
      }, (error) => {
        console.error('Error loading people:', error);
      });

      return () => {
        console.log('Cleaning up people listener');
        unsubscribe();
      };
    }
  }, [isLoggedIn, showTwoFASetup, showTwoFAVerify, loading]);

  const handleLogout = async () => {
    await auth.signOut();
    setIsLoggedIn(false);
    setCurrentPage('dashboard');
  };

  const handleNavigate = (page) => {
    setCurrentPage(page);
  };

  const handleAddPerson = async (personData) => {
    try {
      const newPerson = {
        ...personData,
        id: personData.code,
        stores: personData.stores.map((store, index) => ({
          ...store,
          num: store.num || (index + 1),
          storeCode: `${personData.code}-${String(index + 1).padStart(2, '0')}`,
          name: store.name || `Store ${personData.code}-${String(index + 1).padStart(2, '0')}`,
          bal: store.bal || Math.floor(Math.random() * 50000),
          transactions: store.transactions || []
        }))
      };

      await addPerson(newPerson);
      alert(`Person #${personData.code} added successfully!`);
    } catch (error) {
      console.error('Error adding person:', error);
      alert('Failed to add person. Please try again.');
    }
  };

  const handleUpdatePerson = async (updatedData) => {
    try {
      const updatedPerson = {
        ...updatedData,
        id: updatedData.code,
        stores: (updatedData.stores || []).map((store, index) => ({
          ...store,
          num: store.num || (index + 1),
          storeCode: store.storeCode || `${updatedData.code}-${String(index + 1).padStart(2, '0')}`,
          name: store.name || `Store ${updatedData.code}-${String(index + 1).padStart(2, '0')}`,
          bal: store.bal || 0,
          transactions: store.transactions || []
        }))
      };

      await updatePerson(updatedPerson);
    } catch (error) {
      console.error('Error updating person:', error);
      alert('Failed to update person. Please try again.');
    }
  };

  const handleDeletePerson = async (personCode) => {
    try {
      await deletePerson(personCode);
    } catch (error) {
      console.error('Error deleting person:', error);
      alert('Failed to delete person. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="App">
        <h1>Loading...</h1>
      </div>
    );
  }

  // ÖNCE 2FA kontrolleri yapılmalı!
  if (showTwoFASetup) {
    return <TwoFactorSetup onComplete={() => {
      setShowTwoFASetup(false);
      setIsLoggedIn(true);
    }} />;
  }

  if (showTwoFAVerify) {
    return (
      <TwoFactorVerify 
        onVerified={() => {
          setShowTwoFAVerify(false);
          setIsLoggedIn(true);
        }}
        onCancel={() => {
          auth.signOut();
          setIsLoggedIn(false);
          setShowTwoFAVerify(false);
        }}
      />
    );
  }

  // 2FA tamamlanmadıysa ve login değilse → Login ekranı
  if (!isLoggedIn) {
    return <LoginPage />;
  }

  if (currentPage === 'dashboard') {
    return (
      <Dashboard 
        onLogout={handleLogout}
        onNavigate={handleNavigate}
        userName="Admin"
        people={people}
      />
    );
  }

  if (currentPage === 'stores') {
    return (
      <StoresPage 
        onLogout={handleLogout}
        onNavigate={handleNavigate}
        userName="Admin"
        people={people}
        onUpdatePerson={handleUpdatePerson}
      />
    );
  }

  if (currentPage === 'analytics') {
    return (
      <Analytics 
        onLogout={handleLogout}
        onNavigate={handleNavigate}
        userName="Admin"
        people={people}
      />
    );
  }

  if (currentPage === 'settings') {
    return (
      <SettingsPage 
        onLogout={handleLogout}
        onNavigate={handleNavigate}
        userName="Admin"
        onAddPerson={handleAddPerson}
        people={people}
        onUpdatePerson={handleUpdatePerson}
        onDeletePerson={handleDeletePerson}
      />
    );
  }

  return (
    <div className="App">
      <h1>Page not found: {currentPage}</h1>
      <button onClick={() => handleNavigate('dashboard')}>Go to Dashboard</button>
    </div>
  );
}

export default App;